<template>
  <div class="dev-tip-card bg-white border rounded-lg
   shadow-sm hover:shadow-md transition-shadow duration-200">
    <!-- Card Header -->
    <div class="p-4 border-b">
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-3">
          <!-- Selection Checkbox -->
          <Checkbox
            :model-value="isSelected"
            @change="$emit('select', tip._id)"
            :binary="true"
          />

          <!-- Status Badge -->
          <span
            :class="[
              'px-2 py-1 text-xs rounded-full font-medium',
              tip.active
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600'
            ]"
          >
            {{ tip.active ? 'Active' : 'Inactive' }}
          </span>

                    <!-- Dimension Badge -->
          <span
            v-if="isValidDimension(tip.dimensionValue)"
            class="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium"
            :title="`Valid dimension: ${tip.dimensionValue}`"
          >
            {{ tip.dimensionValue }}
          </span>

          <!-- Invalid Dimension Indicator -->
          <span
            v-else-if="tip.dimensionValue && tip.dimensionValue.trim()"
            class="px-2 py-1 text-xs bg-orange-100 text-orange-800
             rounded-full font-medium border border-orange-300"
            :title="`Invalid dimension: '${tip.dimensionValue}' not found in system`"
          >
            <i class="pi pi-exclamation-triangle mr-1"></i>
            {{ tip.dimensionValue }}
          </span>

          <!-- No Dimension Indicator -->
          <span
            v-else
            class="px-2 py-1 text-xs bg-red-100 text-red-800
             rounded-full font-medium border border-red-300"
            title="This DevTip has no dimension assigned"
          >
            <i class="pi pi-exclamation-triangle mr-1"></i>
            No Dimension
          </span>
        </div>

        <!-- Action Menu -->
        <div class="flex items-center gap-2">
          <Button
            icon="pi pi-pencil"
            class="p-button-text p-button-sm"
            @click="$emit('edit', tip)"
            title="Edit Tip"
          />
          <Button
            :icon="tip.active ? 'pi pi-pause' : 'pi pi-play'"
            :class="[
              'p-button-text p-button-sm',
              tip.active ? 'p-button-warning' : 'p-button-success'
            ]"
            @click="$emit('toggle-status', tip)"
            :title="tip.active ? 'Deactivate' : 'Activate'"
          />
          <Button
            icon="pi pi-trash"
            class="p-button-text p-button-danger p-button-sm"
            @click="$emit('delete', tip)"
            title="Delete Tip"
          />
        </div>
      </div>
    </div>

    <!-- Card Content -->
    <div class="p-4">
      <!-- Title -->
      <div class="mb-3" v-if="tip.title && (tip.title.en || tip.title.de)">
        <h3 class="text-lg font-semibold text-gray-800 mb-1">
          {{ tip.title.en || 'No Title' }}
        </h3>
        <p class="text-sm text-gray-600" v-if="tip.title.de">
          {{ tip.title.de }}
        </p>
      </div>

      <!-- No Title Indicator -->
      <div class="mb-3" v-else>
        <h3 class="text-lg font-semibold text-gray-500 italic">
          No Title
        </h3>
      </div>

      <!-- Content -->
      <div class="mb-3">
        <p class="text-gray-700 text-sm leading-relaxed">
          {{ tip.text.en }}
        </p>
        <p class="text-gray-600 text-sm mt-2 leading-relaxed">
          {{ tip.text.de }}
        </p>
      </div>

      <!-- Metadata -->
      <div class="flex items-center justify-between text-xs text-gray-500">
        <div class="flex items-center gap-4">
          <span>
            <i class="pi pi-calendar mr-1"></i>
            Created: {{ formatDate(tip.createdAt) }}
          </span>
          <span v-if="tip.updatedAt !== tip.createdAt">
            <i class="pi pi-clock mr-1"></i>
            Updated: {{ formatDate(tip.updatedAt) }}
          </span>
        </div>

        <div class="flex items-center gap-2">
          <span class="flex items-center gap-1">
            <i class="pi pi-lightbulb"></i>
            DevTip
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';

export default {
  name: 'DevTipCard',
  components: {
    Checkbox,
    Button,
  },
  props: {
    tip: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('dimensions', ['getAllDimensions']),
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    isValidDimension(dimensionValue) {
      if (!dimensionValue || !dimensionValue.trim()) return false;

      const dimensions = this.getAllDimensions;
      if (!dimensions || dimensions.length === 0) return false;

      return dimensions.some((dim) => dim.value === dimensionValue);
    },
  },
};
</script>

<style scoped>
.dev-tip-card {
  transition: all 0.2s ease-in-out;
}

.dev-tip-card:hover {
  transform: translateY(-1px);
}
</style>
